import { EditableTicketType } from '../../../../domain/entity/TicketTypeEntity'
import { TicketTypeRow } from '../TicketTypeRow'
import { generateUuid } from '../../../../utils/uuids'
import { I18nLabelVO } from '../../../../domain/value-object/I18nLabelVO'
import {
  BatchTicketTypeInput,
  CreateTicketTypeInput,
  UpdateTicketTypeInput,
  DeleteTicketTypeInput,
} from '../../../../domain/repository/WbsItemRepository/types'

/**
 * APIから返される EditableTicketType を TicketTypeRow に変換
 * displayOrderがundefinedの場合はそのまま保持（後で採番）
 */
export const convertToListItem = (
  editable: EditableTicketType,
  index: number
): TicketTypeRow => ({
  ...editable,
  displayOrder: editable.displayOrder, // undefinedまたは数値をそのまま保持
})

/**
 * データ変更を検知
 */
export const checkForChanges = (
  currentData: TicketTypeRow[],
  originalData: TicketTypeRow[]
): boolean => {
  if (currentData.length !== originalData.length) {
    return true
  }

  // UUIDベースで比較（並び替えにも対応）
  return currentData.some(current => {
    const original = originalData.find(orig => orig.uuid === current.uuid)

    // 新規追加の行
    if (!original) {
      return true
    }

    // 内容の変更チェック
    if (
      current.name !== original.name ||
      current.iconUrl !== original.iconUrl ||
      current.isUsed !== original.isUsed
    ) {
      return true
    }

    // 並び順の変更チェック
    if (current.displayOrder !== original.displayOrder) {
      return true
    }

    return false
  })
}

/**
 * TicketTypeRowからI18nLabelVOを生成
 */
const createI18nLabelVO = (item: TicketTypeRow): I18nLabelVO => {
  return {
    labelId: generateUuid(),
    labels: [
      {
        languageCode: 'ja',
        label: item.name || '',
      },
    ],
  }
}

/**
 * 現在のデータと元のデータを比較してbatch API用のペイロードを生成
 */
export const createBatchPayload = (
  currentData: TicketTypeRow[],
  originalData: TicketTypeRow[]
): BatchTicketTypeInput => {
  const added: CreateTicketTypeInput[] = []
  const edited: UpdateTicketTypeInput[] = []
  const deleted: DeleteTicketTypeInput[] = []

  // 削除されたアイテムを検出
  originalData.forEach(original => {
    const exists = currentData.find(current => current.uuid === original.uuid)
    if (!exists) {
      deleted.push({ uuid: original.uuid })
    }
  })

  // 新規追加・更新されたアイテムを検出
  currentData.forEach(current => {
    const original = originalData.find(orig => orig.uuid === current.uuid)

    if (!original) {
      // 新規追加
      added.push({
        uuid: current.uuid,
        code: current.code || '',
        nameI18n: createI18nLabelVO(current),
        iconUrl: current.iconUrl,
      })
    } else {
      // 更新チェック（名前、アイコンの変更）
      const hasChanges =
        current.name !== original.name || current.iconUrl !== original.iconUrl

      if (hasChanges) {
        edited.push({
          uuid: current.uuid,
          nameI18n: createI18nLabelVO(current),
          iconUrl: current.iconUrl,
        })
      }
    }
  })

  // 全てのチケットタイプの順番を生成（Tagと同じパターン）
  const order = {
    ticketTypeOrder: currentData.map((row, index) => ({
      ticketTypeUuid: row.uuid,
      displayOrder: index + 1,
    })),
  }

  const payload: BatchTicketTypeInput = {
    order,
  }
  if (added.length > 0) payload.added = added
  if (edited.length > 0) payload.edited = edited
  if (deleted.length > 0) payload.deleted = deleted

  return payload
}
